import { X } from 'lucide-react';

import { PersonaAvatar } from '../persona/persona-avatar';
import type { Spotlight } from '../store/research-room-store';

/** Breathing room between the element and its ring, in screen pixels. */
const PAD_PX = 10;

/**
 * The part of a capture the room is talking about, lit up on the board.
 *
 * Drawn inside the capture card in the card's own pixels, so it pans and
 * zooms with the screenshot for free. Everything that should stay the same
 * size on screen — the ring's weight, its padding, the label — is divided by
 * the zoom, the way the card's own controls are.
 *
 * Two layers: a coral ring that settles in and then breathes, and a label
 * saying who is talking about what. No dim over the rest of the screenshot:
 * darkening it a beat after the ring landed read as a second, jarring event.
 */
export function SpotlightOverlay({
  bare = false,
  leaving = false,
  onClose,
  spotlight,
  zoom,
}: {
  /** A hovered line's place: the ring alone, soft and fading in — no label,
   *  which flashed dark every time a pointer crossed the thread. */
  bare?: boolean;
  /** On its way out: fades rather than vanishing. */
  leaving?: boolean;
  /** Absent for a hover preview, which goes when the pointer does. */
  onClose?: () => void;
  spotlight: Spotlight;
  zoom: number;
}) {
  const { focus, speaker } = spotlight;
  const pad = PAD_PX / zoom;
  const box = {
    height: focus.height + pad * 2,
    left: focus.x - pad,
    top: focus.y - pad,
    width: focus.width + pad * 2,
  };
  const radius = 14 / zoom;

  return (
    <>
      <div
        aria-hidden="true"
        className={[
          'pointer-events-none absolute transition-opacity duration-200 ease-out motion-reduce:animate-none',
          bare
            ? 'opacity-70 duration-300 animate-in fade-in'
            : 'animate-spotlight-in',
          leaving ? '!opacity-0' : '',
        ].join(' ')}
        key={`ring-${spotlight.key}`}
        style={box}
      >
        {/* Colour from classes, weight from the zoom: the ring keeps the
            same 3px on screen however far in or out the board is. */}
        <div
          className={
            bare
              ? 'absolute inset-0 border-brand/70'
              : 'absolute inset-0 border-brand shadow-spotlight-glow animate-spotlight-breathe motion-reduce:animate-none'
          }
          style={{
            borderRadius: radius,
            borderStyle: 'solid',
            borderWidth: 3 / zoom,
          }}
        />
      </div>

      {/* The label sits above the ring, counter-scaled so it reads the same
          at any zoom, and is the one part that takes a click — to put the
          spotlight away. */}
      {/* The entrance plays on the pill inside, never on this wrapper: an
          animated `transform` would replace the one that places it. */}
      {bare ? null : (
        <div
          className="absolute"
          key={`label-${spotlight.key}`}
          style={{
            left: box.left,
            top: box.top,
            // Anchored at the ring's top-left corner: lifted by its own height,
            // then scaled about that corner, so it sits just above the ring at
            // any zoom and never over what it points at.
            transform: `scale(${String(1 / zoom)}) translateY(-100%)`,
            transformOrigin: 'top left',
          }}
        >
          <div className="mb-2 flex animate-spotlight-in items-center gap-2 rounded-pill bg-ink py-1 pr-1 pl-1.5 text-ui whitespace-nowrap text-white shadow-popover motion-reduce:animate-none">
            {speaker ? (
              <PersonaAvatar persona={speaker} size="sm" />
            ) : (
              <span className="flex size-7 items-center justify-center rounded-pill bg-white/15 text-tag font-semibold">
                You
              </span>
            )}
            <span className="font-semibold">
              {speaker ? speaker.name.split(' ')[0] : 'You asked about'}
            </span>
            <span className="max-w-64 truncate text-white/70">
              {focus.label}
            </span>
            {onClose ? (
              <button
                aria-label="Put the spotlight away"
                className="ml-0.5 flex size-6 cursor-pointer items-center justify-center rounded-pill text-white/60 transition hover:bg-white/15 hover:text-white"
                onClick={onClose}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                type="button"
              >
                <X aria-hidden="true" className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
